import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import axiosInstance from "../../services/AxiosInstance";

// Mapa de categorias suportadas. `name`/`description` são mostrados ao
// utilizador; `icon` é uma classe Bootstrap Icons; `accent` é a cor da
// barra/ícone do card. Tipos adicionais que cheguem do backend mas não
// estejam aqui caem num default cinzento com o próprio key como nome.
const LABELS = {
  sales: {
    name: "Vendas",
    description:
      "Notificação quando há nova venda, interna ou marketplace.",
    icon: "bi-cart-check",
    accent: "#198754", // green
  },
  moloni_documents: {
    name: "Documentos Moloni",
    description:
      "Notificação quando há nova nota de crédito, factura cancelada ou apagada no Moloni.",
    icon: "bi-file-earmark-text",
    accent: "#0d6efd", // blue
  },
  reservation_expiring: {
    name: "Reservas a expirar",
    description:
      "Notificação quando uma reserva está prestes a expirar e ainda não foi convertida em venda.",
    icon: "bi-clock-history",
    accent: "#fd7e14", // orange
  },
  task_assigned: {
    name: "Tarefas atribuídas",
    description:
      "Notificação quando te é atribuída uma nova tarefa ou ordem de trabalho.",
    icon: "bi-clipboard-check",
    accent: "#6f42c1", // purple
  },
  dismantle_completed: {
    name: "Desmantelamento concluído",
    description:
      "Notificação quando o processo de desmantelamento de uma viatura é concluído.",
    icon: "bi-tools",
    accent: "#20c997", // teal
  },
};

const FALLBACK = {
  icon: "bi-bell",
  accent: "#6c757d",
};

const Notifications = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [preferences, setPreferences] = useState({});
  const [supported, setSupported] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get("/user/alert-preferences");
      const data = r?.data?.data || {};
      setPreferences(data.preferences || {});
      setSupported(data.supported_types || []);
    } catch (e) {
      toast.error("Erro a carregar preferências.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (alertType, enabled) => {
    setSaving(alertType);
    const old = preferences[alertType];
    setPreferences((p) => ({ ...p, [alertType]: enabled }));
    try {
      await axiosInstance.post("/user/alert-preferences", {
        alert_type: alertType,
        enabled,
      });
    } catch (e) {
      setPreferences((p) => ({ ...p, [alertType]: old ?? true }));
      toast.error("Falha ao guardar.");
    }
    setSaving(null);
  };

  const summary = useMemo(() => {
    const total = supported.length;
    const on = supported.filter((t) => preferences[t] ?? true).length;
    return { total, on };
  }, [supported, preferences]);

  return (
    <div className="container-fluid p-3" style={{ maxWidth: 880 }}>
      <Card className="shadow-sm border-0">
        <CardHeader
          className="bg-white border-0 pt-3 pb-2"
          style={{ borderRadius: "0.5rem 0.5rem 0 0" }}
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h4 className="mb-1 d-flex align-items-center gap-2">
                <i
                  className="bi bi-bell-fill"
                  style={{ color: "#fd7e14" }}
                />
                Notificações
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                Recebes notificações em todos os teus dispositivos com
                sessão activa. Desliga as categorias que não te interessam.
              </p>
            </div>
            {!loading && summary.total > 0 && (
              <Badge
                color="light"
                pill
                className="text-dark border"
                style={{ fontSize: 12, padding: "6px 12px" }}
              >
                {summary.on}/{summary.total} activas
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardBody className="pt-2">
          {loading && (
            <div className="text-center py-4">
              <Spinner size="sm" /> A carregar…
            </div>
          )}

          {!loading && supported.length === 0 && (
            <div className="text-muted text-center py-4">
              Sem categorias disponíveis.
            </div>
          )}

          {!loading && supported.length > 0 && (
            <Form>
              {supported.map((type) => {
                const enabled = preferences[type] ?? true;
                const label = LABELS[type] || {
                  name: type,
                  description: "",
                  ...FALLBACK,
                };
                const accent = label.accent || FALLBACK.accent;
                const icon = label.icon || FALLBACK.icon;
                const isSaving = saving === type;
                return (
                  <div
                    key={type}
                    className="d-flex align-items-center p-3 mb-2 rounded"
                    style={{
                      background: enabled ? "#fbfbfd" : "#f8f9fa",
                      border: `1px solid ${enabled ? "#e9ecef" : "#e9ecef"}`,
                      borderLeft: `4px solid ${enabled ? accent : "#ced4da"}`,
                      transition: "all 150ms ease",
                      opacity: enabled ? 1 : 0.78,
                    }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle me-3"
                      style={{
                        width: 44,
                        height: 44,
                        background: enabled
                          ? `${accent}1a`
                          : "#e9ecef",
                        color: enabled ? accent : "#6c757d",
                        flexShrink: 0,
                      }}
                    >
                      <i className={`bi ${icon}`} style={{ fontSize: 20 }} />
                    </div>
                    <div className="flex-grow-1 me-3" style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#212529",
                          marginBottom: 2,
                        }}
                      >
                        {label.name}
                      </div>
                      <div
                        className="text-muted"
                        style={{ fontSize: 12.5, lineHeight: 1.4 }}
                      >
                        {label.description}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {isSaving && <Spinner size="sm" />}
                      <Input
                        type="switch"
                        id={`switch-${type}`}
                        checked={enabled}
                        disabled={isSaving}
                        onChange={(e) => toggle(type, e.target.checked)}
                        style={{ cursor: "pointer", marginTop: 0 }}
                      />
                    </div>
                  </div>
                );
              })}
            </Form>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default Notifications;
